import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import { useCompanyFilter } from "@/lib/companyFilter";
import { CalendarDays, Check, X, RefreshCw, Filter, Plus } from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
  Cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_OPTIONS = ["All", "Pending", "Approved", "Rejected", "Cancelled"];

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

export default function Leaves() {
  const { companyId, companyOptions, setCompanyId, empParams } = useCompanyFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [filter, setFilter] = useState("All");
  const [decision, setDecision] = useState(null); // {leave, action}
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyForm, setApplyForm] = useState({
    employee_id: "",
    leave_type_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [applyErr, setApplyErr] = useState("");
  const [applySaving, setApplySaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [a, b, c] = await Promise.all([
      api.get("/leaves", { params: { status_filter: filter } }),
      api.get("/employees", { params: empParams }),
      api.get("/leave-types"),
    ]);
    const emps = b.data || [];
    setEmployees(emps);
    const allowed = new Set(emps.map((e) => e.id));
    const leaves = a.data || [];
    setItems(companyId && companyId !== "all" ? leaves.filter((l) => allowed.has(l.employee_id)) : leaves);
    setLeaveTypes(c.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter, companyId]);

  // Nav query params: ?status=Pending&action=apply
  useEffect(() => {
    const status = searchParams.get("status");
    if (status && STATUS_OPTIONS.includes(status)) {
      setFilter(status);
    }
    if (searchParams.get("action") === "apply") {
      setApplyOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const empMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees]
  );
  const ltMap = useMemo(
    () => Object.fromEntries(leaveTypes.map((l) => [l.id, l])),
    [leaveTypes]
  );

  // Phase 14 — sortable
  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(items, {
    initialKey: "start_date",
    initialDir: "desc",
    accessors: {
      employee: (l) => [empMap[l.employee_id]?.basic?.first_name, empMap[l.employee_id]?.basic?.middle_name, empMap[l.employee_id]?.basic?.last_name].filter(Boolean).join(" "),
      type: (l) => ltMap[l.leave_type_id]?.code || "",
      start_date: (l) => l.start_date || "",
      end_date: (l) => l.end_date || "",
    },
  });

  const submitDecision = async () => {
    if (!decision) return;
    await api.patch(`/leaves/${decision.leave.id}/decision`, {
      action: decision.action,
      note,
    });
    setDecision(null);
    setNote("");
    load();
  };

  const closeApply = () => {
    setApplyOpen(false);
    setApplyErr("");
    setApplyForm({ employee_id: "", leave_type_id: "", start_date: "", end_date: "", reason: "" });
  };

  const submitApply = async (e) => {
    e.preventDefault();
    setApplyErr("");
    setApplySaving(true);
    try {
      await api.post("/leaves", {
        employee_id: applyForm.employee_id,
        leave_type_id: applyForm.leave_type_id,
        start_date: applyForm.start_date,
        end_date: applyForm.end_date,
        reason: applyForm.reason,
      });
      closeApply();
      load();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setApplyErr(typeof d === "string" ? d : "Failed to submit");
    } finally {
      setApplySaving(false);
    }
  };

  const empLabel = (e) =>
    `${[e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ")} (${e.basic?.code || ""})`;

  return (
    <AdminLayout title="Leave Requests" breadcrumbs={["Home", "HR", "Leaves"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#226DB4]" /> All Leave Requests
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setApplyOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium"
              data-testid="leaves-apply-btn"
            >
              <Plus className="h-4 w-4" /> Apply Leave
            </button>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white min-w-[150px]"
              data-testid="leaves-company-select"
            >
              {companyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Filter className="h-4 w-4 text-slate-500" />
            <select
              data-testid="leaves-filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white"
            >
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <button
              onClick={load}
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
              data-testid="leaves-refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="employee" active={sortBy} dir={sortDir} onClick={requestSort}>Employee</SortableTH>
                <SortableTH columnKey="type" active={sortBy} dir={sortDir} onClick={requestSort}>Type</SortableTH>
                <SortableTH columnKey="start_date" active={sortBy} dir={sortDir} onClick={requestSort}>From</SortableTH>
                <SortableTH columnKey="end_date" active={sortBy} dir={sortDir} onClick={requestSort}>To</SortableTH>
                <SortableTH columnKey="days" active={sortBy} dir={sortDir} onClick={requestSort}>Days</SortableTH>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Reason</th>
                <SortableTH columnKey="status" active={sortBy} dir={sortDir} onClick={requestSort}>Status</SortableTH>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={5} cols={8} />
              ) : sortedItems.length === 0 ? (
                <EmptyState colSpan={8} message="No leave requests found" />
              ) : (
                sortedItems.map((l) => {
                  const e = empMap[l.employee_id];
                  const lt = ltMap[l.leave_type_id];
                  return (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">
                          {e ? [e.basic.first_name, e.basic.middle_name, e.basic.last_name].filter(Boolean).join(" ") : "—"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {e?.basic?.code}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-[11px] px-2 py-1 rounded font-medium"
                          style={{
                            backgroundColor: (lt?.color || "#226DB4") + "20",
                            color: lt?.color || "#226DB4",
                          }}
                        >
                          {lt?.code || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{formatDisplayDate(l.start_date)}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDisplayDate(l.end_date)}</td>
                      <td className="px-4 py-3 text-slate-600">{l.days}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">
                        {l.reason || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block text-[11px] px-2 py-1 rounded border font-semibold ${
                            STATUS_COLORS[l.status]
                          }`}
                        >
                          {l.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {l.status === "Pending" ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid={`approve-leave-${l.id}`}
                              onClick={() => setDecision({ leave: l, action: "Approved" })}
                              className="px-2.5 py-1 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded font-medium inline-flex items-center gap-1"
                            >
                              <Check className="h-3 w-3" /> Approve
                            </button>
                            <button
                              data-testid={`reject-leave-${l.id}`}
                              onClick={() => setDecision({ leave: l, action: "Rejected" })}
                              className="px-2.5 py-1 text-xs bg-rose-500 hover:bg-rose-600 text-white rounded font-medium inline-flex items-center gap-1"
                            >
                              <X className="h-3 w-3" /> Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {decision && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              {decision.action} Leave Request
            </h3>
            <p className="text-sm text-slate-600">
              Add an optional note for{" "}
              <span className="font-medium">
                {empMap[decision.leave.employee_id]?.basic.first_name}{" "}
                {empMap[decision.leave.employee_id]?.basic.last_name}
              </span>
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note…"
              rows={3}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
              data-testid="decision-note-input"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setDecision(null);
                  setNote("");
                }}
                className="px-4 py-2 text-sm border border-slate-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={submitDecision}
                data-testid="decision-confirm-btn"
                className={`px-4 py-2 text-sm text-white rounded font-medium ${
                  decision.action === "Approved"
                    ? "bg-emerald-500 hover:bg-emerald-600"
                    : "bg-rose-500 hover:bg-rose-600"
                }`}
              >
                Confirm {decision.action}
              </button>
            </div>
          </div>
        </div>
      )}

      {applyOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitApply} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4" data-testid="leaves-apply-modal">
            <h3 className="text-lg font-semibold text-slate-800">Apply Leave</h3>
            <select
              required
              value={applyForm.employee_id}
              onChange={(e) => setApplyForm({ ...applyForm, employee_id: e.target.value })}
              className={inputCls}
              data-testid="apply-leave-employee-select"
            >
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{empLabel(emp)}</option>
              ))}
            </select>
            <select
              required
              value={applyForm.leave_type_id}
              onChange={(e) => setApplyForm({ ...applyForm, leave_type_id: e.target.value })}
              className={inputCls}
              data-testid="apply-leave-type-select"
            >
              <option value="">Select leave type</option>
              {leaveTypes.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input
                required
                type="date"
                value={applyForm.start_date}
                onChange={(e) => setApplyForm({ ...applyForm, start_date: e.target.value })}
                className={inputCls}
                data-testid="apply-leave-start-input"
              />
              <input
                required
                type="date"
                value={applyForm.end_date}
                onChange={(e) => setApplyForm({ ...applyForm, end_date: e.target.value })}
                className={inputCls}
                data-testid="apply-leave-end-input"
              />
            </div>
            <textarea
              rows={3}
              placeholder="Reason (optional)"
              value={applyForm.reason}
              onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })}
              className={inputCls}
            />
            {applyErr && <div className="text-sm text-rose-600">{applyErr}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeApply} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                disabled={applySaving}
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
                data-testid="apply-leave-submit-btn"
              >
                {applySaving ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
